import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { PortalService } from '@vsolv/packages/portal-config/web';
import moment from 'moment';

export enum StatusTypes {
  Welcome,
  EmailSent,
  SigningIn,
  Error,
  FailedToSignIn,
  SalesDisabled,
}

export enum SignInErrorTypes {
  badLink = 'auth/invalid-action-code',
  badEmail = 'auth/invalid-email',
  tokenExpired = 'TOKEN_EXPIRED',
  tokenAlreadyUsed = 'TOKEN_ALREADY_USED',
  emailMismatch = 'EMAIL_MISMATCH',
  tokenNotFound = 'TOKEN_NOT_FOUND',
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-portal-header',
  templateUrl: './welcome-header.component.html',
  standalone: false,
})
export class PortalHeaderComponent implements OnChanges {
  constructor(public portalSvc: PortalService) {}

  @Input() status: StatusTypes = StatusTypes.Welcome;
  @Input() errorType?: string;

  titles: { title: string; subtitle: string } = {
    title: this.getWelcomeMessage(),
    subtitle: `Welcome to the ${this.portalSvc.config.brandName} ${this.portalSvc.config.productName} portal.`,
  };

  private getContactInfo(): string {
    const email = this.portalSvc.config.contactEmail?.trim();
    if (email) {
      return email;
    }
    const phone = this.portalSvc.config.contactPhone?.trim();
    if (phone) {
      return phone;
    }
    return 'your warranty provider';
  }

  getWelcomeMessage() {
    const currentHour = parseInt(moment().format('HH'));
    if (currentHour >= 0 && currentHour < 12) {
      return 'Good Morning!';
    } else if (currentHour >= 12 && currentHour < 15) {
      return 'Good Afternoon!';
    } else {
      return 'Good Evening!';
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['status'] && changes['status'].previousValue != changes['status'].currentValue) {
      switch (changes['status'].currentValue) {
        case StatusTypes.Welcome:
          this.titles = {
            title: this.getWelcomeMessage(),
            subtitle: `Welcome to the ${this.portalSvc.config.brandName} ${this.portalSvc.config.productName} portal.`,
          };
          break;
        case StatusTypes.Error: {
          // Provide specific error messages based on the error type
          let subtitle = `This can happen if the link expired, or has already been used.\nTo access the ${this.portalSvc.config.brandName} ${this.portalSvc.config.productName} portal, enter your email below and we'll send you a new login link`;

          if (this.errorType === SignInErrorTypes.tokenExpired) {
            subtitle =
              'The link was generated more than 24 hours ago and has expired. Please use the form below to request a new link.';
          } else if (this.errorType === SignInErrorTypes.tokenAlreadyUsed) {
            subtitle =
              'The link is one-time use only and has already been used. Please use the form below to request a new link.';
          } else if (this.errorType === SignInErrorTypes.emailMismatch) {
            subtitle =
              'The link is associated with an email address that does not match your local data. Please check that you are using the correct email or request a new link.';
          }

          this.titles = {
            title: "Oops, this link doesn't work!",
            subtitle,
          };
          break;
        }
        case StatusTypes.SigningIn:
          this.titles = {
            title: 'Hang tight.',
            subtitle: 'We are signing you in.',
          };
          break;
        case StatusTypes.FailedToSignIn:
          this.titles = {
            title: "Oops, we couldn't log you in!",
            subtitle: `Please try again or feel free to contact us at ${this.getContactInfo()}`,
          };
          break;
        case StatusTypes.SalesDisabled:
          this.titles = {
            title: 'No warranties on file',
            subtitle: `New sales are not currently enabled for ${this.portalSvc.config.brandName}. Please contact us at ${this.getContactInfo()} for assistance.`,
          };
          break;
        case StatusTypes.EmailSent:
          this.titles = {
            title: 'Thanks!',
            subtitle: "We're sending your email now.",
          };
      }
    }
  }
}
